import { useBusinessEntitiesOfCompany } from '@commenda/api';
import {
  CountryCode,
  FlagComponent,
  Icons,
  SingleCombobox,
  useToggle,
} from '@commenda/kakashi';
import { IBusinessEntity } from '@commenda/types';
import { getEntityDisplayName } from '@commenda/utils';
import { AddEntityModal } from '../Corporation/common/BusinessEntity/AddEntityModal';

export const SelectEntityDropdownNew = ({
  companyId,
  selectedEntityId,
  onSelectEntity,
  filter,
  className,
  showAddEntity,
  showAllEntities,
  hideChevron = false,
}: {
  companyId: number;
  selectedEntityId: number;
  onSelectEntity: (entityId: number) => void;
  filter?: (entity: IBusinessEntity) => boolean;
  className?: string;
  showAddEntity?: boolean;
  showAllEntities?: boolean;
  hideChevron?: boolean;
}) => {
  const addEntityModalVisibility = useToggle();

  const { data: businessEntities } = useBusinessEntitiesOfCompany(companyId);

  if (!businessEntities) return null;

  return (
    <div className={className ?? 'w-fit'}>
      <AddEntityModal
        isOpen={addEntityModalVisibility.visible}
        onClose={addEntityModalVisibility.hide}
        companyId={companyId}
        onSuccess={async (entity) => {
          onSelectEntity(entity.id);
          addEntityModalVisibility.hide();
        }}
      />
      <SingleCombobox
        useScrollArea
        options={[
          ...(showAllEntities
            ? [
              {
                label: 'All entities',
                value: 'ALL',
              },
            ]
            : []),
          ...(showAddEntity
            ? [
              {
                label: 'Add new entity',
                value: 'add-new-entity',
              },
            ]
            : []),
          ...businessEntities.entities
            .filter(filter || (() => true))
            .map((entity) => ({
              label: getEntityDisplayName({
                legalName: entity.generalCorporationInfo?.legalName,
                incorporationCountry:
                  entity.generalCorporationInfo?.incorporationCountry,
              }),
              value: entity.id.toString(),
            })),
        ]}
        value={selectedEntityId === -1 ? 'ALL' : selectedEntityId?.toString()}
        setValue={(entityId: string) => {
          if (entityId === 'add-new-entity') {
            addEntityModalVisibility.toggle();
          } else if (entityId === 'ALL') {
            onSelectEntity(-1);
          } else {
            onSelectEntity(+entityId);
          }
        }}
        canClearSelection={false}
        placeholder="Select an entity"
        searchPlaceholder="Search for an entity"
        _className="!px-md !py-md rounded-sm [&>svg:last-child]:hidden min-w-[360px]"
        renderTrigger={(props) => {
          if (props.selectedOption?.value === 'ALL') {
            return (
              <div className="flex items-center justify-between gap-md min-w-0 overflow-hidden flex-1 w-full">
                <span className="flex txt-medium-bold text-text-primary truncate flex-1">
                  All entities
                </span>
                {!hideChevron && (
                  <Icons.ChevronSelectorVertical className="size-4 shrink-0 text-fg-quaternary" />
                )}
              </div>
            );
          }

          const businessEntity = businessEntities.entities.find(
            (entity) => entity.id === +props.selectedOption.value
          );

          const incorporationCountry =
            businessEntity?.generalCorporationInfo?.incorporationCountry;

          return (
            <div className="flex items-center justify-between gap-md min-w-0 overflow-hidden flex-1 w-full">
              <div className="flex items-center gap-md min-w-0 flex-1">
                <div className="flex items-center gap-md">
                  <FlagComponent
                    country={incorporationCountry as CountryCode}
                    countryName={incorporationCountry}
                    _className="size-6 shrink-0"
                  />
                  <span className="txt-medium-bold text-text-primary truncate flex-1">
                    {props.selectedOption.label}
                  </span>
                </div>
              </div>
              {!hideChevron && (
                <Icons.ChevronSelectorVertical className="size-4 shrink-0 text-fg-quaternary" />
              )}
            </div>
          );
        }}
        renderOption={({ isSelected, option, onSelect }) => {
          const businessEntity = businessEntities.entities.find(
            (entity) => entity.id === +option.value
          );

          const incorporationCountry =
            businessEntity?.generalCorporationInfo?.incorporationCountry;

          if (option.value === 'ALL') {
            return (
              <div className="flex items-center justify-between w-full py-0.5">
                <span className="txt-medium-medium text-button-secondary-fg truncate">
                  All entities
                </span>
                <div className="w-4 shrink-0">
                  {isSelected && <Icons.Check className="size-4" />}
                </div>
              </div>
            );
          }

          if (option.value === 'add-new-entity') {
            return (
              <div className="flex items-center justify-between w-full py-0.5">
                <div className="flex items-center gap-md w-[80%]">
                  <Icons.Plus className="size-4 shrink-0" />
                  <span className="txt-medium-medium text-button-secondary-fg truncate">
                    Add new entity
                  </span>
                </div>
                <div className="w-4 shrink-0">
                  {isSelected && <Icons.Check className="size-4" />}
                </div>
              </div>
            );
          }

          return (
            <div
              className="flex items-center justify-between w-full py-0.5"
              onClick={() => {
                if (option.value === selectedEntityId?.toString()) return;
                onSelect(option.value);
              }}
            >
              <div className="flex items-center gap-md w-[80%]">
                <FlagComponent
                  country={incorporationCountry as CountryCode}
                  countryName={incorporationCountry}
                  _className="size-6 shrink-0"
                />
                <span className="txt-medium-medium text-button-secondary-fg truncate">
                  {option.label}
                </span>
              </div>
              <div className="w-4 shrink-0">
                {isSelected && <Icons.Check className="size-4" />}
              </div>
            </div>
          );
        }}
      />
    </div>
  );
};
